import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import CloseRounded from '@mui/icons-material/CloseRounded';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import useFollowUpDownload from 'src/components/dashboard/DashboardPage/components/ButtonFollowUpDownloadModal/components/useFollowUpDownload';

import { availableDownloadTypes, downloadTypeNames } from '../helper';

import { styles } from './DownloadModalDialog.styles';

export interface ReportModalDialogProps {
  showModal: boolean;
  closeDialog: () => void;
  onDownload: () => void;
}

const DownloadModalDialog: FC<ReportModalDialogProps> = ({
  showModal,
  closeDialog,
  onDownload,
}) => {
  const { t } = useTranslation('followUp');
  const {
    downloadTypes,
    handleCheckboxChange,
    handleDownloadClick,
    isPending,
    selectedDownloadTypes,
  } = useFollowUpDownload(onDownload);

  return (
    <Dialog
      open={showModal}
      onClose={closeDialog}
      PaperProps={{ sx: styles.paper }}
    >
      <DialogTitle component="div">
        <Stack direction="row" sx={{ justifyContent: 'flex-end', px: 4 }}>
          <IconButton sx={styles.closeButton} onClick={closeDialog}>
            <CloseRounded />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2}>
          <Box sx={{ px: 4 }}>
            <Typography variant="h6" sx={{ pb: 2 }}>
              {t('downloadModal.chooseType')}
            </Typography>
            <FormGroup>
              <Stack direction="column" spacing={1} sx={{ ml: -1.125 }}>
                {availableDownloadTypes
                  .filter((downloadTypeId) =>
                    downloadTypes?.includes(downloadTypeId.toString()),
                  )
                  .map((downloadTypeId) => (
                    <FormControlLabel
                      key={downloadTypeId}
                      sx={styles.label}
                      label={t(downloadTypeNames[downloadTypeId])}
                      control={
                        <Checkbox
                          checked={selectedDownloadTypes.includes(
                            downloadTypeId,
                          )}
                          onChange={() => handleCheckboxChange(downloadTypeId)}
                        />
                      }
                    />
                  ))}
              </Stack>
            </FormGroup>
          </Box>
          <Divider />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={1.25} sx={{ px: 4, width: '100%' }}>
          <Button
            variant="cta"
            fullWidth
            disabled={isPending || !selectedDownloadTypes.length}
            onClick={handleDownloadClick}
          >
            {isPending ? (
              <CircularProgress size={20} />
            ) : (
              t('downloadModal.download')
            )}
          </Button>
          <Button
            onClick={closeDialog}
            variant="cta"
            color="secondary"
            fullWidth
          >
            {t('downloadModal.cancel')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadModalDialog;
